import { locale, t } from '../i18n'
import { formatMoney } from './format'
import type { BomSummary, QuoteInputs, QuoteLine, QuoteMode, QuoteResult } from '../types'

/**
 * RATE CARD v0.1 — indicative only.
 *
 * These are deliberately simple multipliers, not a cost model. Replace them with
 * your factory's real matrix before using this for customer-facing quotes.
 *
 * The structure is intentionally transparent: every multiplier is named and
 * can be tuned independently. See docs/quote-model.md for calibration notes.
 */

const AREA_WASTE = 1.12
const BASE_USD_PER_CM2 = 0.085
const PCB_MIN_ORDER = 49
const PCBA_MIN_ORDER = 99

const LAYER_MULT: Record<number, number> = {
  1: 1,
  2: 1,
  4: 2.2,
  6: 3.6,
  8: 5.2,
  10: 7.0,
  12: 8.6,
}

const THICKNESS_MULT: Record<string, number> = {
  '0.6': 1.04,
  '0.8': 1.02,
  '1.0': 1.0,
  '1.2': 1.02,
  '1.6': 1.0,
  '2.0': 1.14,
  '2.4': 1.22,
  '3.0': 1.3,
}

const COPPER_MULT: Record<string, number> = {
  '0.5': 0.96,
  '1': 1.0,
  '2': 1.24,
  '3': 1.55,
}

const FINISH_MULT: Record<string, number> = {
  hasl: 1.0,
  hasl_lf: 1.05,
  osp: 0.96,
  enig: 1.34,
  imm_silver: 1.42,
  imm_tin: 1.24,
  hard_gold: 2.1,
}

const MASK_MULT: Record<string, number> = {
  green: 1.0,
  black: 1.05,
  matte_black: 1.12,
  blue: 1.04,
  red: 1.04,
  white: 1.06,
}

const SILK_MULT: Record<string, number> = {
  white: 1.0,
  black: 1.0,
  none: 0.96,
}

const TRACE_MULT: Record<string, number> = {
  '6': 1.0,
  '5': 1.07,
  '4': 1.16,
  '3': 1.32,
  '2': 1.65,
}

const TEST_COST: Record<string, number> = {
  e_test: 0,
  flying_probe: 28,
  fixture: 85,
}

const LEAD_MULT: Record<string, number> = {
  standard: 1.0,
  express: 1.24,
  rush: 1.55,
  '24h': 2.1,
}

const QTY_BREAKS: Array<[number, number]> = [
  [10, 1.0],
  [50, 0.92],
  [200, 0.85],
  [1000, 0.78],
  [10000, 0.72],
  [Number.POSITIVE_INFINITY, 0.68],
]

export function defaultQuoteInputs(): QuoteInputs {
  return {
    width: 50,
    height: 30,
    layers: 2,
    qty: 10,
    thickness: 1.6,
    copper: 1,
    finish: 'hasl',
    mask: 'green',
    silkscreen: 'white',
    trace: 4,
    testing: 'e_test',
    impedance: false,
    castellation: false,
    lead: 'standard',
    uniqueParts: 8,
    placements: 24,
    sides: 'top',
    technology: 'smt',
    complexity: 'standard',
    assemblyTest: 'aoi',
    coating: 'none',
    boxBuild: false,
    includePcb: true,
  }
}

export function finishLabel(key: string): string {
  return t(`finish.${key}`)
}

export function traceLabel(key: number): string {
  return t(`trace.${key}`)
}

export function leadLabel(mode: QuoteMode, key: string): string {
  return t(`lead.${mode}.${key}`)
}

function qtyFactor(qty: number): number {
  return QTY_BREAKS.find(([max]) => qty <= max)?.[1] ?? 1
}

function clampInputs(inputs: QuoteInputs): QuoteInputs {
  return {
    ...inputs,
    width: Math.max(1, inputs.width || 1),
    height: Math.max(1, inputs.height || 1),
    layers: Math.max(1, Math.min(12, Math.round(inputs.layers || 2))),
    qty: Math.max(1, Math.min(100000, Math.round(inputs.qty || 1))),
    thickness: inputs.thickness || 1.6,
    copper: inputs.copper || 1,
    uniqueParts: Math.max(0, Math.round(inputs.uniqueParts || 0)),
    placements: Math.max(0, Math.round(inputs.placements || 0)),
    trace: inputs.trace || 4,
  }
}

function pcbEstimate(inputs: QuoteInputs): {
  total: number
  unit: number
  fabrication: number
  setup: number
  test: number
  expedite: number
  areaCm2: number
} {
  const areaCm2 = (inputs.width * inputs.height) / 100 * AREA_WASTE
  const layerMult = LAYER_MULT[inputs.layers] ?? 1 + (inputs.layers - 1) * 0.72
  const copperMult = COPPER_MULT[String(inputs.copper)] ?? 1
  const finishMult = FINISH_MULT[inputs.finish] ?? 1
  const maskMult = MASK_MULT[inputs.mask] ?? 1
  const silkMult = SILK_MULT[inputs.silkscreen] ?? 1
  const traceMult = TRACE_MULT[String(inputs.trace)] ?? 1
  const thicknessMult = THICKNESS_MULT[String(inputs.thickness)] ?? 1

  const unitFab =
    areaCm2 *
    layerMult *
    copperMult *
    finishMult *
    maskMult *
    silkMult *
    traceMult *
    thicknessMult *
    BASE_USD_PER_CM2 *
    qtyFactor(inputs.qty)

  const setup =
    42 +
    inputs.layers * 11 +
    (inputs.impedance ? 45 : 0) +
    (inputs.castellation ? 60 : 0)
  const test = TEST_COST[inputs.testing] ?? 0
  const fabrication = unitFab * inputs.qty
  const subtotal = fabrication + setup + test
  const expedite = subtotal * ((LEAD_MULT[inputs.lead] ?? 1) - 1)
  const total = Math.max(PCB_MIN_ORDER, subtotal + expedite)

  return {
    total,
    unit: total / inputs.qty,
    fabrication,
    setup,
    test,
    expedite,
    areaCm2: (inputs.width * inputs.height) / 100,
  }
}

function assemblyEstimate(inputs: QuoteInputs, bom: BomSummary | null): {
  total: number
  setup: number
  placement: number
  test: number
  coating: number
  box: number
  components: number
} {
  const techMult = inputs.technology === 'mixed' ? 1.28 : inputs.technology === 'tht' ? 1.18 : 1
  const complexityMult =
    inputs.complexity === 'bga' ? 1.48 : inputs.complexity === 'fine_pitch' ? 1.22 : 1
  const sideMult = inputs.sides === 'both' ? 1.55 : 1

  const setup =
    95 +
    inputs.uniqueParts * 3.2 +
    (inputs.sides === 'both' ? 35 : 0) +
    (inputs.technology === 'mixed' ? 45 : 0) +
    (inputs.complexity === 'fine_pitch' ? 55 : inputs.complexity === 'bga' ? 95 : 0)

  const placement =
    inputs.placements * 0.032 * techMult * complexityMult * sideMult
  const test =
    inputs.assemblyTest === 'xray'
      ? 0.35 + inputs.placements * 0.006
      : inputs.assemblyTest === 'functional'
        ? 2.5
        : 0
  const coating =
    inputs.coating === 'acrylic' ? 0.18 : inputs.coating === 'silicone' ? 0.26 : 0
  const box = inputs.boxBuild ? 7.5 : 0
  const components = (bom?.componentCost ?? 0) * inputs.qty
  const leadMult = inputs.lead === 'standard' ? 1 : inputs.lead === 'express' ? 1.18 : 1.42
  const assemblySubtotal = setup + (placement + test + coating + box) * inputs.qty
  const total = assemblySubtotal * leadMult + components

  return {
    total,
    setup: setup * leadMult,
    placement: placement * inputs.qty * leadMult,
    test: test * inputs.qty * leadMult,
    coating: coating * inputs.qty * leadMult,
    box: box * inputs.qty * leadMult,
    components,
  }
}

export function computeQuote(
  rawInputs: QuoteInputs,
  mode: QuoteMode,
  bom: BomSummary | null = null,
): QuoteResult {
  const inputs = clampInputs(rawInputs)
  const pcb = pcbEstimate(inputs)
  const assembly = assemblyEstimate(inputs, bom)
  const warnings: string[] = []
  const lines: QuoteLine[] = []

  if (mode === 'pcb') {
    lines.push({
      label: t('quote.line.fabrication', { qty: inputs.qty.toLocaleString(locale()) }),
      value: `$${formatMoney(pcb.fabrication)}`,
    })
    lines.push({ label: t('quote.line.setup'), value: `$${formatMoney(pcb.setup)}` })
    if (pcb.test > 0) {
      lines.push({ label: t('quote.line.electricalTest'), value: `$${formatMoney(pcb.test)}` })
    }
    if (pcb.expedite > 0) {
      lines.push({ label: t('quote.line.expedite'), value: `$${formatMoney(pcb.expedite)}` })
    }
    const subtotal = pcb.fabrication + pcb.setup + pcb.test + pcb.expedite
    if (pcb.total > subtotal + 0.005) {
      lines.push({ label: t('quote.line.minOrder'), value: `$${formatMoney(pcb.total - subtotal)}` })
    }
    lines.push({ label: t('quote.line.total'), value: `$${formatMoney(pcb.total, pcb.total >= 1000 ? 0 : 2)}`, emphasis: true })

    if (inputs.layers > 8) warnings.push(t('quote.warn.layers'))
    if (inputs.trace <= 3) warnings.push(t('quote.warn.fineLine'))
    if (inputs.finish === 'hard_gold') warnings.push(t('quote.warn.hardGold'))
    if (inputs.impedance) warnings.push(t('quote.warn.impedance'))

    return {
      mode,
      total: pcb.total,
      unit: pcb.unit,
      leadTime: leadLabel(mode, inputs.lead),
      areaCm2: pcb.areaCm2,
      lines,
      warnings,
      pcbTotal: pcb.total,
      assemblyTotal: 0,
    }
  }

  const total = Math.max(PCBA_MIN_ORDER, assembly.total + (inputs.includePcb ? pcb.total : 0))
  const leadMult = inputs.lead === 'standard' ? 1 : inputs.lead === 'express' ? 1.18 : 1.42

  if (inputs.includePcb) {
    lines.push({ label: t('quote.line.barePcb'), value: `$${formatMoney(pcb.total)}` })
  }
  lines.push({ label: t('quote.line.assemblySetup'), value: `$${formatMoney(assembly.setup)}` })
  lines.push({
    label: t('quote.line.assembly', { qty: inputs.placements.toLocaleString(locale()) }),
    value: `$${formatMoney(assembly.placement)}`,
  })
  if (assembly.test > 0) lines.push({ label: t('quote.line.assemblyTest'), value: `$${formatMoney(assembly.test)}` })
  if (assembly.coating > 0) lines.push({ label: t('quote.line.coating'), value: `$${formatMoney(assembly.coating)}` })
  if (assembly.box > 0) lines.push({ label: t('quote.line.box'), value: `$${formatMoney(assembly.box)}` })
  if (assembly.components > 0) {
    lines.push({ label: t('quote.line.components'), value: `$${formatMoney(assembly.components)}` })
  }
  const subtotal = lines.reduce((sum, line) => sum + Number(line.value.replace(/[$,]/g, '')), 0)
  if (total > subtotal + 0.005) {
    lines.push({ label: t('quote.line.minOrder'), value: `$${formatMoney(total - subtotal)}` })
  }
  lines.push({ label: t('quote.line.total'), value: `$${formatMoney(total, total >= 1000 ? 0 : 2)}`, emphasis: true })

  if (!bom || bom.componentCost === null) {
    warnings.push(t('quote.warn.componentsExcluded'))
  }
  if (!bom) {
    warnings.push(t('quote.warn.noBom'))
  }
  if (inputs.lead === '24h') {
    warnings.push(t('quote.warn.24h'))
  }
  if (inputs.placements === 0) {
    warnings.push(t('quote.warn.zeroPlacements'))
  }

  return {
    mode,
    total,
    unit: total / inputs.qty,
    leadTime: leadLabel(mode, inputs.lead),
    areaCm2: pcb.areaCm2,
    lines,
    warnings,
    pcbTotal: pcb.total,
    assemblyTotal: assembly.total,
  }
}
